import { PingHaug } from '../../../@resources/help';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { KeyExpression } from '../../../platform/input/KeyExpression';
import { KeyInput } from '../../../platform/input/KeyInput';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Colors } from '../../../ui';
import { Camera } from '../../canvas/Camera';
import { EditorSession } from '../../canvas/EditorSession';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { Physicals } from '../../canvas/entities/Physicals';
import { CommonShortcutToolBehavior } from '../../canvas/tooling/CommonShortcutToolBehavior';
import { PanZoomToolBehavior } from '../../canvas/tooling/PanZoomToolBehavior';
import { SlapToolBehavior } from '../../canvas/tooling/SlapToolBehavior';
import { ToolBehavior } from '../../canvas/tooling/ToolBehavior';
import { defineTool } from '../../canvas/tooling/ToolDefinition';
import { ToolOverlay } from '../../canvas/tooling/ToolOverlay';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { resolve } from '../../Container';
import { HelpController } from '../../core/help/HelpController';
import { HelpTip } from '../../core/help/HelpTip';
import { ProfileModel } from '../../ProfileModel';
import { Pingable } from './Pingable';
import { PingStore } from './PingStore';
import { pingCanvas } from './PresentCommands';

const pingExpr = KeyExpression.parse('TAB');
type PingRegion = { from?: Point, to?: Point };

export class PingToolBehavior extends ToolBehavior {

    @resolve(Camera) protected camera!: Camera;
    @resolve(CommandDispatcher) protected dispatch!: CommandDispatcher;
    @resolve(ProfileModel) protected me!: ProfileModel;
    @resolve(EditorSession) protected session!: EditorSession;
    @resolve(SpatialIndex) protected spatialIndex!: SpatialIndex;
    @resolve(ToolOverlay) private overlay!: ToolOverlay;
    @resolve(PingStore) private pings!: PingStore;

    private mousePosition = null as Point | null;
    private region = {} as PingRegion;

    public init() {
        this.overlay.add(this.paintRegion.bind(this));
    }

    public onKeyDown(state: string, e: KeyInput) {
        if (pingExpr.matches(e)) {
            if (state === 'idle' || state === 'pinging') {
                e.squash();
            }
            if (state === 'idle') {
                this.beginPing();
                return 'pinging';
            }
        }
    }

    public onKeyUp(state: string, e: KeyInput) {
        if (state === 'pinging') {
            e.squash();
            this.completePing();
            return 'idle';
        }
    }

    public onMouseMove(state: string, mi: MouseInput): string | void {
        this.mousePosition = mi.position;

        if (state === 'pinging') {
            this.updateRegion({ to: this.camera.toModel(mi.position) });
        }
    }

    protected beginPing() {
        if (!this.mousePosition) return;
        const mpt = this.camera.toModel(this.mousePosition);
        this.updateRegion({ from: mpt });
    }

    protected completePing() {

        const mpt = this.camera.toModel(this.mousePosition);
        this.updateRegion({ to: mpt });

        // Only submit a region ping if the distance between from and to is over threshold
        const { from, to } = this.region;
        if (!from || !to) return;

        if (from.distanceTo(to) > 5) {
            this.dispatch(pingCanvas, [from.x, from.y, to.x, to.y]);
        }
        else {
            // For a non-region ping, detect if we pinged on a pingable element and if so, adjust ping location to be the top center of
            // the element.
            const pingPt = this.determinePointPingLocation(from);
            this.dispatch(pingCanvas, [pingPt.x, pingPt.y]);
        }

        this.updateRegion(null);
    }

    private determinePointPingLocation(from: Point) {

        const target = this.getHighestPingableElementAt(from);
        if (target) {
            const tb = target.bounds(RelativeTo.Model);
            return new Point(tb.center().x, tb.top - 8);
        }

        return from;
    }

    private getHighestPingableElementAt(from: Point) {

        const comparer = Physicals.sorter;
        const candidates = this.spatialIndex.lookup(from);
        let result = null as Physical | null;

        for (const x of candidates) {
            if (!x.test(from)) continue;
            if (!x.is(Pingable)) continue;
            if (!!result && comparer(result, x) > 0) continue;
            result = x;
        }

        return result;
    }

    private updateRegion(value: Partial<PingRegion>) {
        this.region = !!value ? { ...this.region, ...value } : {};
        this.overlay.repaint();
    }

    private paintRegion(gfx: UniversalSurfaceRenderer) {
        const { from, to } = this.region ?? {};
        if (!from || !to) return;

        const rect = Rect.fromPoints([
            this.camera.toView(from),
            this.camera.toView(to),
        ]);

        gfx.lineWidth = 4;
        gfx.strokeStyle = this.me.color ?? Colors.primary;
        gfx.strokeRoundRect(rect.left + 2, rect.top + 2, rect.width - 4, rect.height - 4, 3);
    }
}

const pingHaugTip = HelpTip(
    'pingHaug',
    'videos',
    'The Ping Gesture',
    'We\'ve recommended a tutorial video for the Ping Gesture.'
);

export class ExtendedPingToolBehavior extends PingToolBehavior {

    @resolve(HelpController) private help!: HelpController;

    public init() {
        super.init();
        if (this.help.recommendVideo(PingHaug)) {
            this.help.openTip(pingHaugTip);
        }
    }

    public getPreferredCursor() {
        return 'crosshair';
    }

    public onMouseDown(state: string, e: MouseInput): string | void {
        if (state === 'idle' && e.button === MouseButtons.Main) {
            this.beginPing();
            return 'pinging';
        }
    }

    public onMouseUp(state: string): string | void {
        if (state === 'pinging') {
            this.completePing();
            return 'idle';
        }
    }
}

export const pingTool = defineTool({
    id: 'pingTool',
    label: 'Ping',
    category: 'gestures',
    icon: 'deqo://icons/i8/lightning-bolt',
    weight: 4010,
    shortcut: '1',
    behaviors: [
        CommonShortcutToolBehavior,
        ExtendedPingToolBehavior,
        PanZoomToolBehavior(),
        SlapToolBehavior,
    ]
});
